import React from 'react';
import { formatCurrency } from '@shared/formatCurrency';
import { formatPercent } from '@shared';

interface InsuranceQuoteData {
  premiumAmount: number;
  monthlyLTCBenefit: number;
  annualLTCBenefit: number;
  deathBenefit: number;
  totalLTCBenefit: number;
  expectedROI: number;
}

interface InsuranceQuoteComparisonProps {
  initialQuote: InsuranceQuoteData;
  updatedQuote: InsuranceQuoteData;
  carrierName: string;
}

interface QuoteFieldConfig {
  key: keyof InsuranceQuoteData;
  label: string;
  formatter: (value: number) => string;
  shouldShow?: (value: number) => boolean;
}

interface QuoteColumnProps {
  title: string;
  quote: InsuranceQuoteData;
  titleClassName: string;
  textClassName: string;
  valueClassName: string;
}

const QuoteColumn: React.FC<QuoteColumnProps> = ({
  title,
  quote,
  titleClassName,
  textClassName,
  valueClassName,
}) => {
  const formatPercentage = (value: number) => formatPercent(value / 100, 1);

  const fields: QuoteFieldConfig[] = [
    { key: 'premiumAmount', label: 'Premium Amount', formatter: formatCurrency },
    { 
      key: 'monthlyLTCBenefit', 
      label: 'Monthly LTC Benefit', 
      formatter: formatCurrency,
      shouldShow: (value) => value != null && value > 0
    },
    { 
      key: 'annualLTCBenefit', 
      label: 'Annual LTC Benefit', 
      formatter: formatCurrency,
      shouldShow: (value) => value != null && value > 0
    },
    { key: 'deathBenefit', label: 'Death Benefit', formatter: formatCurrency },
    { key: 'totalLTCBenefit', label: 'Total LTC Benefit', formatter: formatCurrency },
    { key: 'expectedROI', label: 'Expected ROI', formatter: formatPercentage },
  ];

  return (
    <div className="space-y-2">
      <p className={titleClassName}>{title}</p>
      <div className="space-y-1">
        {fields.map((field) => {
          const value = quote[field.key];
          const shouldShow = field.shouldShow ? field.shouldShow(value) : true;
          
          if (!shouldShow) return null;
          
          return (
            <p key={field.key} className={textClassName}>
              {field.label}: <span className={valueClassName}>{field.formatter(value)}</span>
            </p>
          );
        })}
      </div>
    </div>
  );
};

export const InsuranceQuoteComparison: React.FC<InsuranceQuoteComparisonProps> = ({
  initialQuote,
  updatedQuote,
}) => {
  return (
    <div className="grid grid-cols-2 gap-4 rounded-lg bg-white p-4">
      <QuoteColumn
        title="Initial Estimate"
        quote={initialQuote}
        titleClassName="text-xs font-medium text-gray-500"
        textClassName="text-sm text-gray-600"
        valueClassName="font-semibold"
      />
      <QuoteColumn
        title="Updated Quote"
        quote={updatedQuote}
        titleClassName="text-xs font-medium text-primary"
        textClassName="text-sm text-gray-900"
        valueClassName="font-bold text-primary"
      />
    </div>
  );
};